import { useSelector } from 'react-redux';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';

import { PsButtonTuner } from 'stencilSetup';
import { PageContent } from '../PageContentDetails/PageContentWrapper';
import { BenefitDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { usePageContentQuery } from 'enrollment/queries/usePageContentQuery';
import { useBenefitPlansQuery } from 'enrollment/queries';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import {
  hasContentBlocks,
  hasRequiredPageContent,
} from '../PageContentDetails/utils';
import './PageContentHeaderDisplay.css';
import { useConfigurationQuery } from 'theSource/queries';
import { ThemedHeader } from 'components/ThemedHeader';
import { ToggleBooleanState } from 'components/ToggleBooleanState';
import { OrbitDrawer } from '../Shared/OrbitDrawer';

interface PageContentHeaderDisplayProps {
  benefit?: BenefitDto['benefit'];
  displayPageHeader?: boolean;
  pageContentLookupCode: string;
  orgPlanYearId?: number;
  lumosApiHost?: string;
}

export const PageContentHeaderDisplay = ({
  benefit,
  lumosApiHost,
  displayPageHeader = true,
  pageContentLookupCode,
  orgPlanYearId,
}: PageContentHeaderDisplayProps) => {
  const contextLookup = useSelector(selectEnrollmentContext);
  const { configuration } = useConfigurationQuery();
  const apiHost = lumosApiHost || configuration?.apiHost;

  const [pageContentSummary, setPageContentSummary] = useState(
    benefit?.page_content_summary || ''
  );
  const [displayLearnMore, setDisplayLearnMore] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = ToggleBooleanState(false) as [
    boolean,
    (value?: boolean) => void,
  ];

  const { plansData, isLoadingPlans, isFetchingPlans } = useBenefitPlansQuery({
    orgBenefitId: benefit?.org_benefit_id,
    enrollmentContextType: contextLookup,
    dependentIds: [],
    enabled: Boolean(apiHost && benefit),
    lumosApiHost: apiHost,
    keepPreviousData: true,
    includeContentDetails: true,
    includeEligibleDependentsData: true,
  });

  const { pageContent } = usePageContentQuery({
    pageContentLookupCode: pageContentLookupCode,
    orgPlanYearId: benefit ? benefit.org_plan_year_id : orgPlanYearId,
    benefitLookupCode: benefit ? benefit.benefit_lookup_code : undefined,
    orgPlanIds: benefit
      ? plansData?.data.plans.map((plan) => plan.plan_id)?.toString()
      : undefined,
    enabled:
      pageContentLookupCode === 'ENROLL_COVERAGE'
        ? Boolean(benefit && plansData)
        : Boolean(pageContentLookupCode),
  });

  useEffect(() => {
    if (pageContent) {
      const pageContentSummary = pageContent?.data.page_text
        ?.replace(/<[^>]*>?/gm, ' ')
        .replace(/\s+/g, ' ')
        ?.replace(/&amp;/g, '&')
        .trim();
      setPageContentSummary(pageContentSummary || '');

      setDisplayLearnMore(
        Boolean(
          hasContentBlocks(pageContent) || hasRequiredPageContent(pageContent)
        )
      );
    }
  }, [pageContent]);

  const [isTruncated, setIsTruncated] = useState(false);

  const textRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const { scrollHeight, clientHeight } = node;
        setIsTruncated(scrollHeight > clientHeight);
      }
    },
    [pageContentSummary]
  );

  if (isLoadingPlans || isFetchingPlans) {
    return <></>;
  }

  const hasDefaultPageContent =
    benefit?.page_content_summary === '< Insert Agreement >';
  const hasNoContent =
    benefit?.page_content_summary === '' || !pageContent?.data.page_text;

  const isNotBenefitHeader = benefit?.org_benefit_name !== '';

  // Extract heading logic for OrbitDrawer into a named variable
  const orbitDrawerHeading = ['ENROLL_COVERAGE', 'sub_main/confirm'].includes(
    pageContentLookupCode
  )
    ? `${t('common:enrollment:pageContentModalTitle1')} ${benefit?.org_benefit_name || t('common:your')} ${t('common:enrollment:pageContentModalTitle2')}`
    : pageContent?.data.page_subtitle || '';

  return (
    <>
      {displayPageHeader ? (
        <ThemedHeader cssClass="plan-select-header">
          {benefit?.org_benefit_name}
        </ThemedHeader>
      ) : !isNotBenefitHeader ? (
        <h2 data-testid="header-two-page-content" className="benefit-header">
          {benefit?.org_benefit_name}
        </h2>
      ) : (
        <span data-testid="header-two-page-content"></span>
      )}
      <div
        className={`description-adjustments ${hasDefaultPageContent || hasNoContent ? 'no-description' : ''}`}
      >
        <div
          ref={textRef}
          className="description-container description-short"
          data-testid="page-content-benefit-header"
        >
          {!hasDefaultPageContent && pageContentSummary}
        </div>
        {(displayLearnMore || isTruncated) && (
          <div className="my-3">
            <PsButtonTuner
              buttonId="learnMore"
              label={t('common:LearnMore')}
              level="secondary"
              onClick={() => setIsDrawerOpen(true)}
            />
            <OrbitDrawer
              drawerId="plan_select_drawer"
              heading={orbitDrawerHeading}
              dialogLarge={true}
              isOpen={isDrawerOpen}
              handleClose={() => setIsDrawerOpen(false)}
            >
              <div className="mt-3">
                <PageContent pageContent={pageContent} showTitle={false} />
              </div>
            </OrbitDrawer>
          </div>
        )}
      </div>
    </>
  );
};
